import type { TokenRefreshConfig } from '../types/config';

/**
 * Default token refresh configuration.
 * Provides sensible defaults for cold start resilience and permission refresh.
 * All values can be overridden via the `config` option in CreateAxiosClientOptions.
 */
export const DEFAULT_TOKEN_REFRESH_CONFIG: TokenRefreshConfig = {
  retryStrategies: {
    // Backend cold start - most retries with longer delays
    coldStart: {
      enabled: true,
      maxAttempts: 5,
      delays: [1000, 2000, 4000, 8000, 16000], // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      timeout: 15000,
      userMessage:
        "🌍 Eco-Friendly Mode Active! 🌱\n\nWe turn off our servers when not in use to save energy and reduce costs.\n\n💡 Would you leave your lights on when you're not home? Neither would we! 😊\n\n⏳ Waking up the system now...\n(Attempt {{attempts}} of 5)",
      shouldLogout: false,
      shouldShowRetryUI: true,
      showMessageAfterFirstRetry: true, // Only show UI after first retry fails
      telemetryLabel: "cold_start",
    },

    // Request timeout - moderate retries with shorter delays
    timeout: {
      enabled: true,
      maxAttempts: 2,
      delays: [500, 1000], // Faster retries for timeouts
      timeout: 10000,
      userMessage: "Request timed out. Please check your connection.",
      shouldLogout: false,
      shouldShowRetryUI: true,
      telemetryLabel: "timeout",
    },

    // Network connectivity errors
    networkError: {
      enabled: true,
      maxAttempts: 3,
      delays: [1000, 2000, 4000],
      timeout: 15000,
      userMessage:
        "Network connection issue. Please check your internet connection.",
      shouldLogout: false,
      shouldShowRetryUI: true,
      telemetryLabel: "network_error",
    },

    // Permission refresh (403) - ONE attempt only
    permissionRefresh: {
      enabled: true,
      maxAttempts: 1,
      delays: [0], // No delay for permission refresh
      timeout: 15000,
      userMessage: "Access denied. Please contact your administrator.",
      shouldLogout: false,
      shouldShowRetryUI: false, // Silent retry for permissions
      telemetryLabel: "permission_refresh",
    },

    // Authentication failures - no retry, immediate logout
    authFailure: {
      enabled: false, // No retry for auth failures
      maxAttempts: 0,
      delays: [],
      timeout: 15000,
      userMessage: "Your session has expired. Please log in again.",
      shouldLogout: true,
      shouldShowRetryUI: false,
      telemetryLabel: "auth_failure",
    },

    // Unknown errors - cautious retry
    unknown: {
      enabled: true,
      maxAttempts: 1,
      delays: [1000],
      timeout: 15000,
      userMessage: "An unexpected error occurred. Please try again.",
      shouldLogout: false,
      shouldShowRetryUI: true,
      telemetryLabel: "unknown_error",
    },
  },

  timeouts: {
    authEndpoint: 15000, // Auth refresh should complete within 15s
    defaultRequest: 15000, // Standard request timeout
  },

  tokenRefreshBuffer: 10, // Refresh token 10 seconds before expiry

  telemetry: {
    enabled: false, // Disabled by default, can be enabled by consumers
  },
};
